import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, html, list } from "../lib/bind";
import { formatDate, shiftYears } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import {
  type PlBasis,
  type PlCategoryAmount,
  type PlReport,
  type VatSource,
  getPlReport,
  plReportCsvUrl,
  plReportPackageUrl,
  plReportPdfUrl,
} from "../services/pl";
import { buildReportsSubNav } from "./reports-subnav";

function amountCell(amount: string): string {
  return formatMoney(amount);
}

function comparisonCell(amount: string | null): string {
  return amount === null ? "—" : formatMoney(amount);
}

/** The label carries the source: the three figures answer different questions, and a
 * bare "VAT" row would leave the reader guessing which one they're looking at. */
const VAT_LINE_LABELS: Record<VatSource, string> = {
  payments: "Less VAT paid to HMRC (payments made in period)",
  locked_periods: "Less VAT due to HMRC (filed VAT returns)",
  computed: "Less VAT due to HMRC (computed \u2014 no filed return covers this period)",
};

const LOAN_TYPE_LABELS: Record<string, string> = {
  bank_loan: "Bank loan",
  directors_loan_in: "Director's loan",
  other: "Other",
};

export function reportsView(): Node {
  const loadError = signal("");
  const loading = signal(false);
  const report = signal<PlReport | null>(null);
  const comparisonEnabled = signal(true);
  const basis = signal<PlBasis>("cash");
  const fromDate = signal("");
  const toDate = signal("");

  function reload(): void {
    loadError.value = "";
    loading.value = true;
    getPlReport({
      startDate: fromDate.value || undefined,
      endDate: toDate.value || undefined,
      comparison: comparisonEnabled.value,
      basis: basis.value,
    })
      .then((result) => {
        report.value = result;
        fromDate.value = result.start_date;
        toDate.value = result.end_date;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load report";
      })
      .finally(() => {
        loading.value = false;
      });
  }
  reload();

  /** Move the whole period a year in either direction, keeping its alignment to the
   * company's year end — the report's own dates are shifted, not recomputed, so a
   * 1 Jun–31 May year steps to the 1 Jun–31 May before it. */
  function stepPeriodYears(delta: number): void {
    if (!fromDate.value || !toDate.value) return;
    fromDate.value = shiftYears(fromDate.value, delta);
    toDate.value = shiftYears(toDate.value, delta);
    reload();
  }

  const showComparisonCol = computed(() => report.value?.comparison_start_date !== null);
  const showReport = computed(() => report.value !== null && !loading.value && loadError.value === "");

  const incomeLines = computed(() => report.value?.income_lines ?? []);
  const cogsLines = computed(() => report.value?.cogs_lines ?? []);
  const opexLines = computed(() => report.value?.opex_lines ?? []);
  const equipmentPurchases = computed(() => report.value?.equipment_purchases ?? []);
  const loans = computed(() => report.value?.loans ?? []);

  const uncategorisedNote = computed(() => {
    const count = report.value?.uncategorised_count ?? 0;
    return count > 0 ? `${count} uncategorised transaction(s) in this period were excluded.` : "";
  });

  const showReconciliation = computed(() => report.value?.basis_reconciliation != null);
  const ctPortions = computed(() => report.value?.corporation_tax.portions ?? []);
  // Hidden rather than shown-as-zero when the rates aren't configured: a working that
  // can't be computed must not look like one that came out at nil.
  const showCtWorking = computed(() => report.value?.corporation_tax.tax != null);
  const ctUnavailable = computed(() => report.value?.corporation_tax.unavailable_reason ?? "");
  const ctSalaryNote = computed(() => report.value?.corporation_tax.salary_hygiene_note ?? "");

  const basisHint = computed(() =>
    basis.value === "accrual"
      ? "Turnover is counted when invoiced. Expenses stay on the cash basis — the app records no purchase invoices, so there is no accrual date for them."
      : "Income and expenses are counted when money moves through the bank.",
  );

  const reconciliationWarning = computed(() => {
    const rec = report.value?.basis_reconciliation;
    if (!rec || rec.reconciles) return "";
    return "The difference between the two bases does not equal the movement in debtors. Usually a receipt against an invoice categorised outside turnover, or an invoice written off during the period.";
  });

  const frsNote = computed(() => {
    const r = report.value;
    if (!r) return "";
    const computedNote =
      r.frs_surplus.source === "computed"
        ? " The VAT due is computed from invoices — no filed VAT return covers this period."
        : "";
    return (
      `VAT charged ${formatMoney(r.frs_surplus.vat_charged)} − ` +
      `VAT due to HMRC ${formatMoney(r.frs_surplus.vat_paid_to_hmrc)} = ` +
      `surplus ${formatMoney(r.frs_surplus.surplus)} — what the flat rate earned on this ` +
      `period's sales, already in Operating profit above.${computedNote}`
    );
  });

  const fragment = html`<div class="view">
    <div class="card">
      <div class="page-header">
        <span class="card-label">Profit &amp; Loss</span>
        <div class="invoice-actions report-actions">
          <a class="btn download-csv-link" target="_blank" rel="noopener">Download CSV</a>
          <a class="btn download-pdf-link" target="_blank" rel="noopener">Download PDF</a>
          <a class="btn download-package-link">Download package</a>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="pl-from">From</label>
          <input id="pl-from" type="date" />
        </div>
        <div class="form-field">
          <label for="pl-to">To</label>
          <input id="pl-to" type="date" />
        </div>
        <div class="form-actions">
          <button class="btn period-prev-btn" type="button">Previous year</button>
          <button class="btn period-next-btn" type="button">Next year</button>
        </div>
        <div class="form-field">
          <label for="pl-basis">Basis</label>
          <select id="pl-basis">
            <option value="cash">Cash (income when received)</option>
            <option value="accrual">Accrual (income when invoiced)</option>
          </select>
        </div>
        <div class="form-field checkbox-field">
          <input id="pl-comparison" type="checkbox" checked />
          <label for="pl-comparison">Compare to prior year</label>
        </div>
      </div>
      <p class="field-hint basis-hint"></p>
      <p class="form-error"></p>
    </div>

    <div class="card report-content">
      <div class="card-label-row"><span class="card-label">Income</span></div>
      <div class="data-table">
        <table>
          <thead><tr><th>Category</th><th>Amount</th><th class="cmp-col">Prior year</th></tr></thead>
          <tbody class="income-body"></tbody>
        </table>
      </div>
      <table class="invoice-totals">
        <tr class="invoice-total-row">
          <td>Total income</td><td class="income-total"></td><td class="cmp-col income-total-cmp"></td>
        </tr>
      </table>

      <div class="card-label-row"><span class="card-label">Cost of goods</span></div>
      <div class="data-table">
        <table>
          <thead><tr><th>Category</th><th>Amount</th><th class="cmp-col">Prior year</th></tr></thead>
          <tbody class="cogs-body"></tbody>
        </table>
      </div>
      <table class="invoice-totals">
        <tr class="invoice-total-row">
          <td>Gross profit</td><td class="gross-profit"></td><td class="cmp-col gross-profit-cmp"></td>
        </tr>
      </table>

      <div class="card-label-row"><span class="card-label">Operating expenses</span></div>
      <div class="data-table">
        <table>
          <thead><tr><th>Category</th><th>Amount</th><th class="cmp-col">Prior year</th></tr></thead>
          <tbody class="opex-body"></tbody>
        </table>
      </div>
      <table class="invoice-totals">
        <tr class="invoice-total-row">
          <td>Loan interest</td><td class="loan-interest"></td><td class="cmp-col loan-interest-cmp"></td>
        </tr>
        <tr class="invoice-total-row">
          <td class="vat-hmrc-label"></td><td class="vat-hmrc"></td><td class="cmp-col vat-hmrc-cmp"></td>
        </tr>
        <tr class="invoice-total-row">
          <td>Operating profit</td><td class="operating-profit"></td><td class="cmp-col operating-profit-cmp"></td>
        </tr>
      </table>

      <div class="basis-reconciliation">
        <div class="card-label-row"><span class="card-label">Basis reconciliation</span></div>
        <table class="invoice-totals">
          <tr class="invoice-total-row"><td>Turnover invoiced</td><td class="rec-invoiced"></td></tr>
          <tr class="invoice-total-row"><td>Turnover received (cash basis)</td><td class="rec-cash"></td></tr>
          <tr class="invoice-total-row"><td>Difference</td><td class="rec-difference"></td></tr>
          <tr class="invoice-total-row"><td>Debtors at period start</td><td class="rec-debtors-start"></td></tr>
          <tr class="invoice-total-row"><td>Debtors at period end</td><td class="rec-debtors-end"></td></tr>
          <tr class="invoice-total-row"><td>Movement in debtors</td><td class="rec-debtors-movement"></td></tr>
        </table>
        <p class="form-error rec-warning"></p>
      </div>

      <p class="field-hint frs-note"></p>

      <div class="card-label-row"><span class="card-label">Memo (excluded from the taxable figure above)</span></div>
      <table class="invoice-totals">
        <tr class="invoice-total-row"><td>Non-allowable expenses</td><td class="non-allowable-total"></td></tr>
        <tr class="invoice-total-row">
          <td>Equipment / capital purchases (candidate AIA items)</td><td class="equipment-total"></td>
        </tr>
        <tr class="invoice-total-row"><td>Owner drawings</td><td class="owner-drawings-total"></td></tr>
        <tr class="invoice-total-row"><td>Corporation Tax paid</td><td class="tax-paid-total"></td></tr>
        <tr class="invoice-total-row"><td class="transfers-label"></td><td class="transfers-total"></td></tr>
        <tr class="invoice-total-row"><td>Loan principal repaid</td><td class="loan-principal-total"></td></tr>
        <tr class="invoice-total-row"><td>Loan drawdowns received</td><td class="loan-drawdowns-total"></td></tr>
      </table>
      <div class="data-table equipment-purchases-table">
        <table>
          <thead><tr><th>Date</th><th>Description</th><th>Amount</th></tr></thead>
          <tbody class="equipment-body"></tbody>
        </table>
      </div>

      <div class="card-label-row"><span class="card-label">Outstanding loan balances at period end</span></div>
      <div class="data-table loans-table">
        <table>
          <thead><tr><th>Lender</th><th>Type</th><th>Outstanding balance</th></tr></thead>
          <tbody class="loans-body"></tbody>
        </table>
      </div>

      <p class="field-hint uncategorised-note"></p>
    </div>

    <div class="card ct-card">
      <div class="card-label-row"><span class="card-label">Corporation Tax estimate</span></div>
      <p class="ct-caveat">
        <strong>Indicative estimate — confirm with your accountant.</strong>
        This is not a filing figure.
      </p>
      <table class="invoice-totals">
        <tr class="invoice-total-row"><td>Operating profit</td><td class="ct-operating-profit"></td></tr>
        <tr class="invoice-total-row"><td>Less capital allowances (AIA)</td><td class="ct-capital-allowances"></td></tr>
        <tr class="invoice-total-row"><td>Taxable total profits</td><td class="ct-ttp"></td></tr>
      </table>
      <div class="ct-working">
        <table class="invoice-totals">
          <tr class="invoice-total-row"><td>Associated companies</td><td class="ct-associates"></td></tr>
          <tr class="invoice-total-row"><td>Period length</td><td class="ct-days"></td></tr>
        </table>
        <div class="data-table ct-portions-table">
          <table>
            <thead><tr><th>Financial year</th><th>Profit</th><th>Tax</th></tr></thead>
            <tbody class="ct-portions-body"></tbody>
          </table>
        </div>
        <table class="invoice-totals">
          <tr class="invoice-total-row"><td>Marginal relief</td><td class="ct-marginal-relief"></td></tr>
          <tr class="invoice-total-row"><td>Estimated Corporation Tax</td><td class="ct-tax"></td></tr>
          <tr class="invoice-total-row"><td>Effective rate</td><td class="ct-effective-rate"></td></tr>
        </table>
      </div>
      <p class="form-error ct-unavailable"></p>
      <p class="field-hint ct-assumptions">
        Assumes every capital purchase qualifies for AIA, and that augmented profits equal
        taxable total profits (the app has no concept of franked investment income). Losses
        brought forward are not modelled.
      </p>
      <p class="field-hint ct-salary-note"></p>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("reports view produced no .view element");
  viewEl.insertBefore(buildReportsSubNav("pl"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const reportContent = fragment.querySelector<HTMLElement>(".report-content");
  const fromInput = fragment.querySelector<HTMLInputElement>("#pl-from");
  const toInput = fragment.querySelector<HTMLInputElement>("#pl-to");
  const periodPrevBtn = fragment.querySelector<HTMLButtonElement>(".period-prev-btn");
  const periodNextBtn = fragment.querySelector<HTMLButtonElement>(".period-next-btn");
  const comparisonInput = fragment.querySelector<HTMLInputElement>("#pl-comparison");
  const basisSelect = fragment.querySelector<HTMLSelectElement>("#pl-basis");
  const basisHintEl = fragment.querySelector<HTMLElement>(".basis-hint");
  const reconciliationEl = fragment.querySelector<HTMLElement>(".basis-reconciliation");
  const recInvoicedEl = fragment.querySelector<HTMLElement>(".rec-invoiced");
  const recCashEl = fragment.querySelector<HTMLElement>(".rec-cash");
  const recDifferenceEl = fragment.querySelector<HTMLElement>(".rec-difference");
  const recDebtorsStartEl = fragment.querySelector<HTMLElement>(".rec-debtors-start");
  const recDebtorsEndEl = fragment.querySelector<HTMLElement>(".rec-debtors-end");
  const recDebtorsMovementEl = fragment.querySelector<HTMLElement>(".rec-debtors-movement");
  const recWarningEl = fragment.querySelector<HTMLElement>(".rec-warning");
  const csvLink = fragment.querySelector<HTMLAnchorElement>(".download-csv-link");
  const pdfLink = fragment.querySelector<HTMLAnchorElement>(".download-pdf-link");
  const packageLink = fragment.querySelector<HTMLAnchorElement>(".download-package-link");

  const incomeTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[0];
  const incomeBody = fragment.querySelector<HTMLTableSectionElement>(".income-body");
  const cogsTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[1];
  const cogsBody = fragment.querySelector<HTMLTableSectionElement>(".cogs-body");
  const opexTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[2];
  const opexBody = fragment.querySelector<HTMLTableSectionElement>(".opex-body");
  const equipmentTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[3];
  const equipmentBody = fragment.querySelector<HTMLTableSectionElement>(".equipment-body");
  const loansTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[4];
  const loansBody = fragment.querySelector<HTMLTableSectionElement>(".loans-body");
  const ctPortionsTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[5];
  const ctPortionsBody = fragment.querySelector<HTMLTableSectionElement>(".ct-portions-body");
  const ctCard = fragment.querySelector<HTMLElement>(".ct-card");
  const ctWorking = fragment.querySelector<HTMLElement>(".ct-working");
  const ctOperatingProfitEl = fragment.querySelector<HTMLElement>(".ct-operating-profit");
  const ctCapitalAllowancesEl = fragment.querySelector<HTMLElement>(".ct-capital-allowances");
  const ctTtpEl = fragment.querySelector<HTMLElement>(".ct-ttp");
  const ctAssociatesEl = fragment.querySelector<HTMLElement>(".ct-associates");
  const ctDaysEl = fragment.querySelector<HTMLElement>(".ct-days");
  const ctMarginalReliefEl = fragment.querySelector<HTMLElement>(".ct-marginal-relief");
  const ctTaxEl = fragment.querySelector<HTMLElement>(".ct-tax");
  const ctEffectiveRateEl = fragment.querySelector<HTMLElement>(".ct-effective-rate");
  const ctUnavailableEl = fragment.querySelector<HTMLElement>(".ct-unavailable");
  const ctSalaryNoteEl = fragment.querySelector<HTMLElement>(".ct-salary-note");

  const incomeTotalEl = fragment.querySelector<HTMLElement>(".income-total");
  const incomeTotalCmpEl = fragment.querySelector<HTMLElement>(".income-total-cmp");
  const grossProfitEl = fragment.querySelector<HTMLElement>(".gross-profit");
  const grossProfitCmpEl = fragment.querySelector<HTMLElement>(".gross-profit-cmp");
  const loanInterestEl = fragment.querySelector<HTMLElement>(".loan-interest");
  const loanInterestCmpEl = fragment.querySelector<HTMLElement>(".loan-interest-cmp");
  const operatingProfitEl = fragment.querySelector<HTMLElement>(".operating-profit");
  const operatingProfitCmpEl = fragment.querySelector<HTMLElement>(".operating-profit-cmp");
  const nonAllowableEl = fragment.querySelector<HTMLElement>(".non-allowable-total");
  const equipmentTotalEl = fragment.querySelector<HTMLElement>(".equipment-total");
  const ownerDrawingsEl = fragment.querySelector<HTMLElement>(".owner-drawings-total");
  const vatHmrcLabelEl = fragment.querySelector<HTMLElement>(".vat-hmrc-label");
  const vatHmrcEl = fragment.querySelector<HTMLElement>(".vat-hmrc");
  const vatHmrcCmpEl = fragment.querySelector<HTMLElement>(".vat-hmrc-cmp");
  const taxPaidEl = fragment.querySelector<HTMLElement>(".tax-paid-total");
  const transfersLabelEl = fragment.querySelector<HTMLElement>(".transfers-label");
  const transfersTotalEl = fragment.querySelector<HTMLElement>(".transfers-total");
  const loanPrincipalEl = fragment.querySelector<HTMLElement>(".loan-principal-total");
  const loanDrawdownsEl = fragment.querySelector<HTMLElement>(".loan-drawdowns-total");
  const frsNoteEl = fragment.querySelector<HTMLElement>(".frs-note");
  const uncategorisedNoteEl = fragment.querySelector<HTMLElement>(".uncategorised-note");

  if (
    !errorEl ||
    !reportContent ||
    !fromInput ||
    !periodPrevBtn ||
    !periodNextBtn ||
    !toInput ||
    !comparisonInput ||
    !basisSelect ||
    !basisHintEl ||
    !reconciliationEl ||
    !recInvoicedEl ||
    !recCashEl ||
    !recDifferenceEl ||
    !recDebtorsStartEl ||
    !recDebtorsEndEl ||
    !recDebtorsMovementEl ||
    !recWarningEl ||
    !csvLink ||
    !pdfLink ||
    !packageLink ||
    !incomeTable ||
    !incomeBody ||
    !cogsTable ||
    !cogsBody ||
    !opexTable ||
    !opexBody ||
    !equipmentTable ||
    !equipmentBody ||
    !loansTable ||
    !loansBody ||
    !ctPortionsTable ||
    !ctPortionsBody ||
    !ctCard ||
    !ctWorking ||
    !ctOperatingProfitEl ||
    !ctCapitalAllowancesEl ||
    !ctTtpEl ||
    !ctAssociatesEl ||
    !ctDaysEl ||
    !ctMarginalReliefEl ||
    !ctTaxEl ||
    !ctEffectiveRateEl ||
    !ctUnavailableEl ||
    !ctSalaryNoteEl ||
    !incomeTotalEl ||
    !incomeTotalCmpEl ||
    !grossProfitEl ||
    !grossProfitCmpEl ||
    !loanInterestEl ||
    !loanInterestCmpEl ||
    !operatingProfitEl ||
    !operatingProfitCmpEl ||
    !nonAllowableEl ||
    !equipmentTotalEl ||
    !ownerDrawingsEl ||
    !vatHmrcLabelEl ||
    !vatHmrcEl ||
    !vatHmrcCmpEl ||
    !taxPaidEl ||
    !transfersLabelEl ||
    !transfersTotalEl ||
    !loanPrincipalEl ||
    !loanDrawdownsEl ||
    !frsNoteEl ||
    !uncategorisedNoteEl
  ) {
    throw new Error("reports view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);
  bindShow(reportContent, showReport);
  bindShow(ctCard, showReport);
  bindShow(ctWorking, showCtWorking);
  bindErrorText(ctUnavailableEl, ctUnavailable);
  bindText(ctSalaryNoteEl, ctSalaryNote);
  bindShow(reconciliationEl, showReconciliation);
  bindText(frsNoteEl, frsNote);
  bindText(basisHintEl, basisHint);
  bindErrorText(recWarningEl, reconciliationWarning);
  bindText(uncategorisedNoteEl, uncategorisedNote);

  for (const cmpEl of fragment.querySelectorAll<HTMLElement>(".cmp-col")) {
    bindShow(cmpEl, showComparisonCol);
  }

  currentScope().effect(() => {
    const r = report.value;
    if (!r) return;
    incomeTotalEl.textContent = amountCell(r.income_total);
    incomeTotalCmpEl.textContent = comparisonCell(r.income_comparison_total);
    grossProfitEl.textContent = amountCell(r.gross_profit);
    grossProfitCmpEl.textContent = comparisonCell(r.gross_profit_comparison);
    loanInterestEl.textContent = amountCell(r.loan_interest_total);
    loanInterestCmpEl.textContent = comparisonCell(r.loan_interest_comparison_total);
    operatingProfitEl.textContent = amountCell(r.operating_profit);
    operatingProfitCmpEl.textContent = comparisonCell(r.operating_profit_comparison);
    nonAllowableEl.textContent = amountCell(r.non_allowable_total);
    equipmentTotalEl.textContent = amountCell(r.equipment_purchases_total);
    ownerDrawingsEl.textContent = amountCell(r.owner_drawings_total);
    vatHmrcLabelEl.textContent = VAT_LINE_LABELS[r.vat_to_hmrc.source];
    vatHmrcEl.textContent = amountCell(r.vat_to_hmrc.amount);
    vatHmrcCmpEl.textContent = comparisonCell(r.vat_to_hmrc_comparison);
    taxPaidEl.textContent = amountCell(r.tax_paid_total);
    transfersLabelEl.textContent = `Transfers between own accounts (${String(r.transfers_count)})`;
    transfersTotalEl.textContent = amountCell(r.transfers_total);
    loanPrincipalEl.textContent = amountCell(r.loan_principal_repaid_total);
    loanDrawdownsEl.textContent = amountCell(r.loan_drawdowns_received_total);

    const ct = r.corporation_tax;
    ctOperatingProfitEl.textContent = amountCell(ct.operating_profit);
    ctCapitalAllowancesEl.textContent = amountCell(ct.capital_allowances);
    ctTtpEl.textContent = amountCell(ct.taxable_total_profits);
    ctAssociatesEl.textContent = String(ct.associated_companies_count);
    ctDaysEl.textContent = `${String(ct.days_in_period)} days`;
    ctMarginalReliefEl.textContent = ct.marginal_relief ? amountCell(ct.marginal_relief) : "—";
    ctTaxEl.textContent = ct.tax ? amountCell(ct.tax) : "—";
    ctEffectiveRateEl.textContent = ct.effective_rate ? `${ct.effective_rate}%` : "—";

    const rec = r.basis_reconciliation;
    if (rec) {
      recInvoicedEl.textContent = amountCell(rec.invoiced_turnover);
      recCashEl.textContent = amountCell(rec.cash_turnover);
      recDifferenceEl.textContent = amountCell(rec.difference);
      recDebtorsStartEl.textContent = amountCell(rec.debtors_at_start);
      recDebtorsEndEl.textContent = amountCell(rec.debtors_at_end);
      recDebtorsMovementEl.textContent = amountCell(rec.debtors_movement);
    }
  });

  currentScope().effect(() => {
    fromInput.value = fromDate.value;
    toInput.value = toDate.value;
  });

  currentScope().effect(() => {
    const params = {
      startDate: fromDate.value || undefined,
      endDate: toDate.value || undefined,
      comparison: comparisonEnabled.value,
      basis: basis.value,
    };
    csvLink.href = plReportCsvUrl(params);
    pdfLink.href = plReportPdfUrl(params);
    packageLink.href = plReportPackageUrl(params);
  });

  currentScope().effect(() => {
    basisSelect.value = basis.value;
  });

  periodPrevBtn.addEventListener("click", () => {
    stepPeriodYears(-1);
  });
  periodNextBtn.addEventListener("click", () => {
    stepPeriodYears(1);
  });
  // Disabled in flight: reload() writes the server's period back into the inputs, so a
  // second click before the first response lands would be reverted by it (the same race
  // recorded in 08-roadmap.md § Deferred engineering follow-ups for typed edits). These
  // buttons exist to be clicked repeatedly, which makes that race easy to hit.
  currentScope().effect(() => {
    const busy = loading.value || !fromDate.value || !toDate.value;
    periodPrevBtn.disabled = busy;
    periodNextBtn.disabled = busy;
  });

  fromInput.addEventListener("change", () => {
    fromDate.value = fromInput.value;
    reload();
  });
  toInput.addEventListener("change", () => {
    toDate.value = toInput.value;
    reload();
  });
  comparisonInput.addEventListener("change", () => {
    comparisonEnabled.value = comparisonInput.checked;
    reload();
  });
  basisSelect.addEventListener("change", () => {
    basis.value = basisSelect.value === "accrual" ? "accrual" : "cash";
    reload();
  });

  function categoryRow(line: PlCategoryAmount, table: HTMLTableElement): Node {
    const row = html`<tr>
      <td>${line.category_name}</td>
      <td>${amountCell(line.amount)}</td>
      <td class="cmp-col">${comparisonCell(line.comparison_amount)}</td>
    </tr>`.firstElementChild;
    if (!row) throw new Error("P/L category row template produced no element");
    for (const cell of row.querySelectorAll<HTMLElement>(".cmp-col")) bindShow(cell, showComparisonCol);
    return labelRow(row, table);
  }

  // The accrual basis's invoiced-turnover line has no category_id; its name is stable
  // and unique within the section, so it stands in as the list key.
  const lineKey = (line: PlCategoryAmount): string => line.category_id ?? line.category_name;

  list(incomeBody, incomeLines, lineKey, (l) => categoryRow(l, incomeTable));
  list(cogsBody, cogsLines, lineKey, (l) => categoryRow(l, cogsTable));
  list(opexBody, opexLines, lineKey, (l) => categoryRow(l, opexTable));

  list(
    equipmentBody,
    equipmentPurchases,
    (p) => p.transaction_id,
    (p) => {
      const row = html`<tr>
        <td>${formatDate(p.date)}</td>
        <td>${p.description}</td>
        <td>${amountCell(p.amount)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("P/L equipment purchase row template produced no element");
      return labelRow(row, equipmentTable);
    },
  );

  list(
    ctPortionsBody,
    ctPortions,
    (p) => String(p.fy_start_year),
    (p) => {
      const row = html`<tr>
        <td>${`FY${String(p.fy_start_year)} (${String(p.days)} days)`}</td>
        <td>${amountCell(p.profit)}</td>
        <td>${amountCell(p.tax)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("CT portion row template produced no element");
      return labelRow(row, ctPortionsTable);
    },
  );

  list(
    loansBody,
    loans,
    (l) => l.loan_id,
    (l) => {
      const row = html`<tr>
        <td>${l.lender_name}</td>
        <td>${LOAN_TYPE_LABELS[l.loan_type] ?? l.loan_type}</td>
        <td>${amountCell(l.outstanding_balance)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("P/L loan memo row template produced no element");
      return labelRow(row, loansTable);
    },
  );

  return fragment;
}
